import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { api, setRobotsOverride } from "@/lib/api";
import { ga4Enabled, ga4PageView, initGa4 } from "@/lib/ga4";
import { Header, Footer } from "@/components/site/Chrome";
import { MedicalHeader, MedicalFooter } from "@/components/medical/MedicalChrome";
import MedicalHome from "@/pages/medical/MedicalHome";
import MedicalCategory from "@/pages/medical/MedicalCategory";
import Home from "@/pages/Home";
import HubPage from "@/pages/HubPage";
import RecreationPage from "@/pages/RecreationPage";
import ComfortPage from "@/pages/ComfortPage";
import LinksPage from "@/pages/LinksPage";
import GuidePage from "@/pages/GuidePage";
import ToolsIndex from "@/pages/ToolsIndex";
import Partners from "@/pages/Partners";
import StaticPage, { ContactPage } from "@/pages/StaticPage";
import GolfFitChecker from "@/pages/tools/GolfFitChecker";
import SaunaPlanner from "@/pages/tools/SaunaPlanner";
import GeneratorEstimator from "@/pages/tools/GeneratorEstimator";
import ProjectorFitChecker from "@/pages/tools/ProjectorFitChecker";
import SleepSelector from "@/pages/tools/SleepSelector";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminLayout from "@/pages/admin/AdminLayout";
import Dashboard from "@/pages/admin/Dashboard";
import Analytics from "@/pages/admin/Analytics";
import AdminForgotPassword from "@/pages/admin/AdminForgotPassword";
import AdminResetPassword from "@/pages/admin/AdminResetPassword";
import GrowthResearch from "@/pages/admin/GrowthResearch";
import ArticlesAdmin from "@/pages/admin/ArticlesAdmin";
import OffersAdmin from "@/pages/admin/OffersAdmin";
import MerchandisingAdmin from "@/pages/admin/MerchandisingAdmin";
import SourcesAdmin from "@/pages/admin/SourcesAdmin";
import ContactAdmin from "@/pages/admin/ContactAdmin";
import EmailLayout from "@/pages/admin/email/EmailLayout";
import EmailOverview from "@/pages/admin/email/EmailOverview";
import EmailSubscribers from "@/pages/admin/email/EmailSubscribers";
import EmailTags from "@/pages/admin/email/EmailTags";
import EmailCampaigns from "@/pages/admin/email/EmailCampaigns";
import EmailAutoresponder from "@/pages/admin/email/EmailAutoresponder";
import EmailSettings from "@/pages/admin/email/EmailSettings";
import PerfectChairPC610 from "@/pages/PerfectChairPC610";
import EmailPreferences from "@/pages/EmailPreferences";
import { EmailSignupManager } from "@/components/site/EmailSignup";
import NotFound from "@/pages/NotFound";
import SaunaCloudGallery from "@/pages/SaunaCloudGallery";
import { initAffiliateConversionTracking } from "@/lib/affiliateConversion";
import { scrollToSectionId } from "@/lib/scrollToSection";
import "@/App.css";

const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    // The browser's own scroll restoration otherwise fights the resets below on SPA navigation.
    if ("scrollRestoration" in window.history) window.history.scrollRestoration = "manual";
  }, []);
  useEffect(() => {
    if (!hash) {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      return;
    }
    // Content loads async, so poll briefly for the target section before giving up. Landing
    // straight on a hash (ad traffic) is positioned instantly and clear of the sticky header, then
    // re-corrected as late-loading imagery settles the layout.
    const id = hash.slice(1);
    let tries = 0;
    let timers = [];
    let reposition = null;
    const poll = setInterval(() => {
      if (scrollToSectionId(id, { smooth: false })) {
        clearInterval(poll);
        // Imagery above the target keeps settling for a second or two; re-apply on each image load
        // (capture phase catches <img>) and a few times after, then stop interfering.
        reposition = () => scrollToSectionId(id, { smooth: false });
        document.addEventListener("load", reposition, true);
        timers = [400, 1200, 2500, 4000].map((d) => setTimeout(reposition, d));
        timers.push(
          setTimeout(() => document.removeEventListener("load", reposition, true), 4200)
        );
      } else if (++tries > 40) {
        clearInterval(poll);
      }
    }, 100);
    return () => {
      clearInterval(poll);
      timers.forEach(clearTimeout);
      if (reposition) document.removeEventListener("load", reposition, true);
    };
  }, [pathname, hash]);
  return null;
};

// Pinterest page event: one per route, including the initial load. The base tag in index.html only
// initializes, so nothing is double-counted. Guarded by path so React StrictMode's double effect
// invocation in development cannot fire twice. No user data is ever passed.
let pinterestLastPath = null;

const PinterestPageView = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    if (pinterestLastPath === pathname) return;
    pinterestLastPath = pathname;
    window.pintrk?.("page");
  }, [pathname]);
  return null;
};

// Google Ads route updates, tag AW-1000124803. The inline config in index.html measures the initial
// load, so the first effect run is skipped and nothing is double-counted. `config` is the documented
// command for an Ads destination. Page views are never conversions: no 'conversion' event is sent
// from here or anywhere else in the app. Admin paths are not measured.
let gadsLastPath = null;

const GoogleAdsPageView = () => {
  const { pathname, search } = useLocation();
  useEffect(() => {
    if (gadsLastPath === null) {
      gadsLastPath = pathname + search;
      return;
    }
    if (gadsLastPath === pathname + search) return;
    gadsLastPath = pathname + search;
    if (pathname.startsWith("/admin")) return;
    // Page titles are set by each page's own effect, so wait a beat before reporting the route.
    const id = setTimeout(() => {
      window.gtag?.("config", "AW-1000124803", {
        page_path: pathname + search,
        page_location: window.location.href,
        page_title: document.title,
      });
    }, 400);
    return () => clearTimeout(id);
  }, [pathname, search]);
  return null;
};

// Google Analytics 4 route page views. Dormant until REACT_APP_GA4_MEASUREMENT_ID is set. The
// initial load is reported once on mount (the Google tag's own automatic page view is disabled for
// the GA4 destination), then once per route change. Admin paths are not measured.
let ga4LastPath = null;

const Ga4PageView = () => {
  const { pathname, search } = useLocation();
  useEffect(() => {
    if (!ga4Enabled()) return undefined;
    initGa4();
    if (ga4LastPath === pathname + search) return undefined;
    if (pathname.startsWith("/admin")) return undefined;
    // Titles are set by each page's own effect, so report the route a beat later. The guard is
    // written inside the callback so a cancelled timeout never swallows the page view.
    const id = setTimeout(() => {
      ga4LastPath = pathname + search;
      ga4PageView();
    }, 400);
    return () => clearTimeout(id);
  }, [pathname, search]);
  return null;
};

// Robots guard: the backend route allowlist (/api/route-status) is the single source of truth for
// which public paths belong in a search index. Any route it reports as indexable:false — a hidden
// hub, a retired tool — is downgraded to noindex, follow. Nothing is hard-coded per URL, and pages
// that already set a stricter directive (the 404 view, /links) keep their own.
const RobotsGuard = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    setRobotsOverride(null);
    // The admin area is private. robots.txt already disallows it, but a crawler that reaches a
    // link to it from anywhere else gets an explicit refusal too.
    if (pathname.startsWith("/admin")) {
      setRobotsOverride("noindex, nofollow");
      return undefined;
    }
    let live = true;
    api
      .get("/route-status", { params: { path: pathname } })
      .then(({ data }) => {
        if (live && data?.indexable === false) setRobotsOverride("noindex, follow");
      })
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [pathname]);
  return null;
};

// Fires the Google Ads click conversion once per outbound affiliate click. Mounted once, outside
// the router, so route changes never re-register the listener or report a conversion.
const AffiliateConversionTracking = () => {
  useEffect(() => initAffiliateConversionTracking(), []);
  return null;
};

const Shell = ({ children }) => {
  const { pathname, search } = useLocation();
  const isAdmin = pathname.startsWith("/admin");
  if (isAdmin) return children;
  // Medical & Mobility is a self-contained division: its own header, navigation and footer, so a
  // visitor stays inside the section. Shared legal and contact pages reached from that footer
  // carry ?ref=medical-mobility, which keeps the section's chrome (and its return path) in place
  // instead of duplicating the legal content under a second set of URLs.
  const medical =
    pathname === "/medical-mobility" ||
    pathname.startsWith("/medical-mobility/") ||
    new URLSearchParams(search).get("ref") === "medical-mobility";
  return (
    <>
      {medical ? <MedicalHeader /> : <Header />}
      {/* Reserves the viewport before route data arrives, so the footer cannot shift upward and
          then back down while content loads. This was the measured source of the layout shift. */}
      <main id="main-content" className="min-h-[calc(100vh-180px)]">{children}</main>
      {medical ? <MedicalFooter /> : <Footer />}
      {/* Newsletter capture belongs to the main site only: Medical & Mobility stays untouched. */}
      {!medical && <EmailSignupManager />}
    </>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ScrollToTop />
        <PinterestPageView />
        <GoogleAdsPageView />
        <Ga4PageView />
        <AffiliateConversionTracking />
        <RobotsGuard />
        <Toaster position="bottom-right" />
        <Shell>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/hub/recreation" element={<RecreationPage />} />
            <Route path="/hub/comfort-sleep" element={<ComfortPage />} />
            {/* Former combined category URL keeps working and points at the canonical Recreation route. */}
            <Route
              path="/hub/entertainment-recreation"
              element={<Navigate to="/hub/recreation" replace />}
            />
            <Route path="/hub/:slug" element={<HubPage />} />
            <Route path="/guides/:slug" element={<GuidePage />} />
            <Route path="/saunacloud-gallery" element={<SaunaCloudGallery />} />
            {/* Dedicated product page inside the Wellness category. */}
            <Route
              path="/wellness/human-touch-perfect-chair-pc-610"
              element={<PerfectChairPC610 />}
            />
            {/* Medical & Mobility: a dedicated division with its own navigation and footer. */}
            <Route path="/medical-mobility" element={<MedicalHome />} />
            <Route path="/medical-mobility/:slug" element={<MedicalCategory />} />
            <Route path="/tools" element={<ToolsIndex />} />
            {/* Permanent social bio link. Destinations are maintained in backend/links_page.py so
                this URL never has to change. */}
            <Route path="/links" element={<LinksPage />} />            <Route path="/tools/golf-simulator-room-fit" element={<GolfFitChecker />} />
            <Route path="/tools/sauna-project-planner" element={<SaunaPlanner />} />
            <Route path="/tools/generator-coverage-estimator" element={<GeneratorEstimator />} />
            <Route path="/tools/projector-room-fit" element={<ProjectorFitChecker />} />
            {/* The Massage Chair Selector is retired. The route stays so old links and bookmarks
                land on the Wellness & Recovery page instead of a 404. */}
            <Route
              path="/tools/massage-chair-selector"
              element={<Navigate to="/hub/wellness-recovery" replace />}
            />
            <Route path="/tools/sleep-compatibility-selector" element={<SleepSelector />} />
            <Route path="/about" element={<StaticPage />} />
            <Route path="/how-we-help" element={<StaticPage />} />
            <Route path="/editorial-standards" element={<StaticPage />} />
            <Route path="/affiliate-disclosure" element={<StaticPage />} />
            <Route path="/privacy" element={<StaticPage />} />
            <Route path="/terms" element={<StaticPage />} />
            <Route path="/contact" element={<ContactPage />} />
            {/* Destinations of the links inside our emails. */}
            <Route path="/email/confirm" element={<EmailPreferences />} />
            <Route path="/email/preferences" element={<EmailPreferences />} />
            <Route path="/email/unsubscribe" element={<EmailPreferences />} />
            <Route path="/partners" element={<Partners />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/forgot-password" element={<AdminForgotPassword />} />
            <Route path="/admin/reset-password" element={<AdminResetPassword />} />
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<Dashboard />} />
              <Route path="analytics" element={<Analytics />} />
              <Route path="growth" element={<GrowthResearch />} />
              <Route path="articles" element={<ArticlesAdmin />} />
              <Route path="offers" element={<OffersAdmin />} />
              <Route path="merchandising" element={<MerchandisingAdmin />} />
              <Route path="sources" element={<SourcesAdmin />} />
              <Route path="audience" element={<Navigate to="/admin/email/subscribers" replace />} />
              <Route path="email" element={<EmailLayout />}>
                <Route index element={<EmailOverview />} />
                <Route path="subscribers" element={<EmailSubscribers />} />
                <Route path="tags" element={<EmailTags />} />
                <Route path="campaigns" element={<EmailCampaigns />} />
                <Route path="autoresponder" element={<EmailAutoresponder />} />
                <Route path="settings" element={<EmailSettings />} />
              </Route>
              <Route path="contact" element={<ContactAdmin />} />
            </Route>
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Shell>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
